import { useEffect, useRef, useState, useCallback } from 'react';
import { gsap } from 'gsap';
import { TextScramble } from './TextScramble';
import { GridOverlay } from './GridOverlay';
import { CoordinateDisplay } from './CoordinateDisplay';

interface CinematicPreloaderProps {
  onComplete: () => void;
}

export function CinematicPreloader({ onComplete }: CinematicPreloaderProps) {
  const [counter, setCounter] = useState(0);
  const [isAnimating, setIsAnimating] = useState(true);
  const containerRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const nkRef = useRef<HTMLSpanElement>(null);
  const dotRef = useRef<HTMLSpanElement>(null);
  const designRef = useRef<HTMLSpanElement>(null);
  const dotZoomRef = useRef<HTMLDivElement>(null);
  const taglineRef = useRef<HTMLDivElement>(null);
  const progressBarRef = useRef<HTMLDivElement>(null);
  const scanlineRef = useRef<HTMLDivElement>(null);

  // Check for reduced motion preference
  const prefersReducedMotion = useRef(
    typeof window !== 'undefined' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );

  // Master timeline reference
  const masterTL = useRef<gsap.core.Timeline | null>(null);

  const runExitAnimation = useCallback(() => {
    const exitTL = gsap.timeline();

    // Fade out NK and DESIGN (split apart)
    exitTL.to(
      nkRef.current,
      {
        x: '-20vw',
        opacity: 0,
        duration: 0.5,
        ease: 'power3.in',
      },
      0
    );

    exitTL.to(
      designRef.current,
      {
        x: '20vw',
        opacity: 0,
        duration: 0.5,
        ease: 'power3.in',
      },
      0
    );

    // Fade out tagline and progress
    exitTL.to(
      [taglineRef.current, progressBarRef.current?.parentElement],
      {
        opacity: 0,
        duration: 0.3,
        ease: 'power2.in',
      },
      0
    );

    // THE MAIN EFFECT: Green dot zooms to fill screen
    exitTL.to(
      dotRef.current,
      {
        scale: 0,
        opacity: 0,
        duration: 0.2,
        ease: 'power2.in',
      },
      0.1
    );

    // Zoom the full-screen dot overlay
    // Element is 2vmax, needs to cover 150vmax = scale 75
    exitTL.fromTo(
      dotZoomRef.current,
      {
        scale: 0,
        opacity: 1,
      },
      {
        scale: 75,
        opacity: 1,
        duration: 0.7,
        ease: 'power2.in',
      },
      0.2
    );

    // CRITICAL: Fire onComplete when green FILLS screen completely
    // Hero has its own green overlay that will seamlessly continue
    exitTL.call(
      () => {
        onComplete();
      },
      [],
      0.8
    );

    // Simply hide preloader - Hero's green overlay takes over seamlessly
    exitTL.set(
      containerRef.current,
      {
        autoAlpha: 0,
        onComplete: () => {
          setIsAnimating(false);
        },
      },
      0.85
    );

    return exitTL;
  }, [onComplete]);

  useEffect(() => {
    // Handle reduced motion
    if (prefersReducedMotion.current) {
      setTimeout(() => {
        setIsAnimating(false);
        onComplete();
      }, 500);
      return;
    }

    const ctx = gsap.context(() => {
      masterTL.current = gsap.timeline();

      // NK entry
      masterTL.current.fromTo(
        nkRef.current,
        { x: -30, opacity: 0 },
        {
          x: 0,
          opacity: 1,
          duration: 0.5,
          ease: 'power2.out',
        },
        0.3
      );

      // DESIGN entry
      masterTL.current.fromTo(
        designRef.current,
        { x: 30, opacity: 0 },
        {
          x: 0,
          opacity: 1,
          duration: 0.5,
          ease: 'power2.out',
        },
        0.3
      );

      // Emerald dot pulse in
      masterTL.current.fromTo(
        dotRef.current,
        { scale: 0, opacity: 0 },
        {
          scale: 1,
          opacity: 1,
          duration: 0.6,
          ease: 'back.out(2)',
        },
        0.6
      );

      // Dot continuous pulse
      gsap.to(dotRef.current, {
        scale: 1.2,
        duration: 0.8,
        repeat: -1,
        yoyo: true,
        ease: 'sine.inOut',
        delay: 1.2,
      });

      // Tagline fade up
      masterTL.current.fromTo(
        taglineRef.current,
        { y: 20, opacity: 0 },
        {
          y: 0,
          opacity: 1,
          duration: 0.6,
          ease: 'power2.out',
        },
        1.6
      );

      // Counter animation
      const counterObj = { value: 0 };
      masterTL.current.to(
        counterObj,
        {
          value: 100,
          duration: 2.5,
          ease: 'power2.out',
          onUpdate: () => {
            setCounter(Math.floor(counterObj.value));
          },
        },
        0.3
      );

      // Progress bar animation
      masterTL.current.to(
        progressBarRef.current,
        {
          width: '100%',
          duration: 2.5,
          ease: 'power2.out',
        },
        0.3
      );

      // Scanline overlay animation
      gsap.to(scanlineRef.current, {
        backgroundPosition: '0 100%',
        duration: 8,
        repeat: -1,
        ease: 'none',
      });

      // Trigger exit animation
      masterTL.current.call(
        () => {
          runExitAnimation();
        },
        [],
        3.0
      );
    }, containerRef);

    return () => {
      ctx.revert();
      if (masterTL.current) {
        masterTL.current.kill();
      }
    };
  }, [onComplete, runExitAnimation]);

  if (!isAnimating && prefersReducedMotion.current) {
    return null;
  }

  return (
    <div
      ref={containerRef}
      className="fixed inset-0 z-[9999] bg-[#0a0a0a] flex flex-col items-center justify-center overflow-hidden"
      aria-label="Loading portfolio"
      role="progressbar"
      aria-valuenow={counter}
      aria-valuemin={0}
      aria-valuemax={100}
    >
      {/* Grid Overlay */}
      <GridOverlay />

      {/* Coordinates */}
      <CoordinateDisplay startDelay={200} />

      {/* Scanline Overlay */}
      <div
        ref={scanlineRef}
        className="absolute inset-0 pointer-events-none opacity-[0.08]"
        style={{
          backgroundImage: `repeating-linear-gradient(
            0deg,
            transparent 0px,
            transparent 2px,
            rgba(0, 0, 0, 0.3) 2px,
            rgba(0, 0, 0, 0.3) 4px
          )`,
          backgroundSize: '100% 4px',
        }}
        aria-hidden="true"
      />

      {/* Dot Zoom Overlay - for exit animation */}
      {/* Uses vmax to ensure perfect circle covers entire viewport when scaled */}
      <div
        ref={dotZoomRef}
        className="absolute rounded-full bg-emerald-500 pointer-events-none"
        style={{
          width: '2vmax',
          height: '2vmax',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%) scale(0)',
          opacity: 0,
        }}
        aria-hidden="true"
      />

      {/* Main Content */}
      <div ref={contentRef} className="relative z-10 flex flex-col items-center">
        {/* Main Typography: NK.DESIGN */}
        <div className="relative text-[11vw] md:text-[9vw] lg:text-[7vw] font-bold tracking-tight leading-none flex items-center">
          <span ref={nkRef} className="inline-block text-white/90 opacity-0">
            <TextScramble
              text="NK"
              duration={1200}
              delay={400}
              flipSpeed={80}
            />
          </span>
          <span
            ref={dotRef}
            className="inline-block text-emerald-500 mx-2"
            style={{ transform: 'scale(0)', opacity: 0 }}
          >
            .
          </span>
          <span ref={designRef} className="inline-block text-white/90 opacity-0">
            <TextScramble
              text="DESIGN"
              duration={1400}
              delay={400}
              flipSpeed={70}
            />
          </span>
        </div>

        {/* Tagline */}
        <div
          ref={taglineRef}
          className="mt-6 text-xs md:text-sm tracking-[0.25em] text-white/40 uppercase font-mono opacity-0"
        >
          Digital Products That Inspire
        </div>
      </div>

      {/* Progress Section (Bottom Right) */}
      <div className="absolute bottom-8 right-8 md:bottom-12 md:right-12 flex flex-col items-end gap-2">
        {/* Progress Counter */}
        <div className="text-xs font-mono text-white/30 tracking-wider">
          LOADING
          <span className="text-white/50 ml-2 tabular-nums">{counter}%</span>
        </div>

        {/* Progress Bar */}
        <div className="w-40 md:w-60 h-[2px] bg-white/10 overflow-hidden rounded-full">
          <div
            ref={progressBarRef}
            className="h-full w-0 rounded-full"
            style={{
              background: 'linear-gradient(90deg, #10b981 0%, #34d399 50%, #10b981 100%)',
              backgroundSize: '200% 100%',
              animation: 'shimmer 2s linear infinite',
            }}
          />
        </div>
      </div>

      {/* Brand Mark (Bottom Left) */}
      <div className="absolute bottom-8 left-8 md:bottom-12 md:left-12">
        <div className="text-[10px] font-mono text-white/20 tracking-wider">
          © 2025 NK.DESIGN
        </div>
      </div>

      {/* Shimmer animation style */}
      <style>{`
        @keyframes shimmer {
          0% { background-position: 200% 0; }
          100% { background-position: -200% 0; }
        }
      `}</style>
    </div>
  );
}
